import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@/public/icons/close_black.svg';
import ExpandIcon from '@/public/icons/expand.svg';
import CollapseIcon from '@/public/icons/collapse.svg';
import TopToScroll from '@/components/TopToScroll';
import { BoxProps } from '@mui/material/Box';
import classNames from 'classnames';

type ModalInnerProps = {
  contentRef?: React.RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  isSandbox?: boolean;
  title?: string;
  extraNode?: ReactNode;
  onClose: () => void;
  onChangeExpand?: (isExpand: boolean) => void;
  resetExpandKey?: unknown;
  scrollToTopBottomOffset?: number;
  showScrollToTopButton?: boolean;
  maxWidth?: number;
  viewportMargin?: number;
} & BoxProps;

export const ModalInner = forwardRef<HTMLDivElement, ModalInnerProps>(
  (_props, ref) => {
    const {
      children,
      isMobile,
      isSandbox = false,
      title = '',
      contentRef,
      extraNode,
      onClose,
      onChangeExpand,
      resetExpandKey,
      sx,
      scrollToTopBottomOffset,
      showScrollToTopButton = true,
      maxWidth = 1168,
      viewportMargin = 40,
      ...props
    } = _props;

    const [isExpand, setIsExpand] = useState(false);
    const isExpandRef = useRef(isExpand);
    const hasExpandHistoryRef = useRef(false);
    const shouldHandleExpandHistory = isSandbox && !isMobile;

    const collapseExpand = useCallback(
      (syncHistory: boolean) => {
        if (!isExpandRef.current) return;

        if (
          syncHistory &&
          hasExpandHistoryRef.current &&
          window.history.state?.boardSandboxExpand
        ) {
          window.history.back();
          return;
        }

        hasExpandHistoryRef.current = false;
        isExpandRef.current = false;
        setIsExpand(false);
        onChangeExpand?.(false);
      },
      [onChangeExpand],
    );

    useEffect(() => {
      if (isExpandRef.current) {
        if (
          hasExpandHistoryRef.current &&
          window.history.state?.boardSandboxExpand
        ) {
          window.history.back();
          return;
        }

        hasExpandHistoryRef.current = false;
        isExpandRef.current = false;
        onChangeExpand?.(false);
        setIsExpand(false);
        return;
      }

      setIsExpand(false);
    }, [resetExpandKey]);

    useEffect(() => {
      isExpandRef.current = isExpand;
    }, [isExpand]);

    useEffect(() => {
      if (!shouldHandleExpandHistory || !isExpand) return;

      window.history.pushState(
        { ...(window.history.state ?? {}), boardSandboxExpand: true },
        '',
        window.location.href,
      );
      hasExpandHistoryRef.current = true;

      const handlePopState = () => {
        if (!isExpandRef.current || !hasExpandHistoryRef.current) return;

        hasExpandHistoryRef.current = false;
        isExpandRef.current = false;
        setIsExpand(false);
        onChangeExpand?.(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }, [isExpand, onChangeExpand, shouldHandleExpandHistory]);

    useEffect(() => {
      if (!shouldHandleExpandHistory || !isExpand) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        collapseExpand(true);
      };

      window.addEventListener('keydown', handleKeyDown, true);

      return () => {
        window.removeEventListener('keydown', handleKeyDown, true);
      };
    }, [collapseExpand, isExpand, shouldHandleExpandHistory]);

    return (
      <Box
        className={classNames({ isExpand, isSandboxModal: isSandbox })}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: isMobile ? 'auto' : '400px',
          width: isMobile
            ? '100%'
            : `min(calc(100% - ${viewportMargin * 2}px), ${maxWidth}px)`,
          height: isMobile
            ? '100%'
            : isSandbox
            ? `calc(100vh - ${viewportMargin * 2}px)`
            : 'auto',
          maxHeight: isMobile
            ? '100%'
            : `calc(100vh - ${viewportMargin * 2}px)`,
          borderRadius: isMobile ? 0 : '8px',
          overflow: 'hidden',
          padding: 'var(--modal-padding, 0px) var(--modal-padding, 0px) 0',
          backgroundColor: 'var(--dk-bg, white)',
          transition: 'width 0.3s ease, height 0.3s ease',
          '&.isExpand': {
            width: '100%',
            height: '100%',
            maxHeight: '100dvh',
            borderRadius: 0,
          },
          '&.isSandboxModal.isExpand': {
            width: '100vw',
            height: '100dvh',
            maxWidth: '100vw',
            maxHeight: '100dvh',
          },
          '&.isSandboxModal.isExpand .button_wrap': {
            display: 'none',
          },
          '&.isSandboxModal.isExpand .modal-title': {
            display: 'none',
          },
          '&.isSandboxModal.isExpand .modal-content': {
            overflow: 'hidden',
          },
          '&.isSandboxModal.isExpand .isSandbox.isModal': {
            paddingLeft: 0,
            paddingRight: 0,
          },
          '&.isSandboxModal.isExpand .isSandbox > header': {
            display: 'none',
          },
          '&.isSandboxModal.isExpand .isSandbox .board-content-wrap': {
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
          },
          '&.isSandboxModal.isExpand .isSandbox .board-content': {
            paddingTop: '0px',
          },
          '&.isSandboxModal.isExpand .isSandbox .sandbox-bottom-bar': {
            display: 'none',
          },
          '& .sandbox-float-restore': {
            position: 'absolute',
            top: '12px',
            right: '24px',
            zIndex: 200,
            width: '40px',
            height: '40px',
            padding: '8px',
            border: 'none',
            borderRadius: '999px',
            background: 'rgba(0, 0, 0, 0.24)',
            boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.08)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.40)',
            },
          },
          '.button_wrap': {
            position: 'absolute',
            top: isMobile ? '14px' : '20px',
            right: '16px',
            zIndex: 3,
            display: 'block',
            '& button': {
              padding: 0,
              width: isMobile ? 28 : 24,
              svg: { width: isMobile ? 28 : 24, height: isMobile ? 28 : 24 },
            },
          },
          '.modal-title': {
            position: 'relative',
            fontSize: isMobile ? '20px' : '28px',
            fontWeight: '500',
            color: 'var(--dk-text, #000)',
            lineHeight: '32px',
            borderBottom: '2px solid black',
            paddingBottom: '6px',
            zIndex: 3,
          },
          '.modal-content': {
            '&:focus': { outline: 'none' },
            position: 'relative',
            width: '100%',
            height:
              isMobile || isSandbox
                ? 'calc(100% - var(--extra-height, 0px))'
                : 'auto',
            maxHeight:
              !isMobile && !isSandbox
                ? `calc(100vh - ${
                    viewportMargin * 2
                  }px - var(--extra-height, 0px))`
                : undefined,
            overflowY: 'auto',
          },
          ...sx,
        }}
        {...props}
      >
        {title && <Typography className={'modal-title'}>{title}</Typography>}
        {isSandbox && isExpand && !isMobile && (
          <button
            type="button"
            className="sandbox-float-restore"
            aria-label="축소"
            onClick={() => {
              collapseExpand(true);
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M19.082 12.7217C19.4132 12.7219 19.6816 12.991 19.6816 13.3223C19.6813 13.6531 19.4129 13.9216 19.082 13.9219L14.7705 13.9209L19.0254 18.1758C19.2596 18.4101 19.2606 18.7901 19.0264 19.0244C18.7921 19.2587 18.4121 19.2576 18.1777 19.0234L13.8018 14.6475L13.8027 19.2012C13.8025 19.5323 13.5333 19.8017 13.2021 19.8018C12.8711 19.8016 12.6028 19.5322 12.6025 19.2012V13.3223C12.6025 12.991 12.8709 12.7219 13.2021 12.7217H19.082ZM10.6816 4.19727C11.0129 4.19727 11.282 4.46667 11.2822 4.79785V10.6768C11.2822 11.0081 11.013 11.2764 10.6816 11.2764H4.80273C4.47141 11.2763 4.20313 11.0081 4.20312 10.6768C4.20345 10.346 4.47103 10.0777 4.80176 10.0771H9.23145L5.16602 6.01172C4.9317 5.77741 4.9317 5.39838 5.16602 5.16406C5.40033 4.92975 5.77936 4.92975 6.01367 5.16406L10.082 9.23242V4.79785C10.0823 4.46683 10.3506 4.19753 10.6816 4.19727Z"
                fill="white"
              />
            </svg>
          </button>
        )}
        <Stack direction={'row'} spacing={'12px'} className={'button_wrap'}>
          {!isMobile && (
            <Tooltip
              title={isExpand ? '축소해서 보기' : '확장해서 보기'}
              placement="top"
              arrow
              // offset -8 로 말풍선이 버튼 상단을 덮는데, 기본값(interactive)은
              // popper 가 pointer-events:auto 라 그 지점의 클릭을 툴팁이 삼킨다
              disableInteractive
              slotProps={{
                tooltip: { sx: { backgroundColor: 'black', color: 'white' } },
                arrow: { sx: { color: 'var(--dk-text, black)' } },
                popper: {
                  modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
                },
              }}
            >
              <Button
                className={'expand'}
                onClick={() => {
                  if (isMobile) return;
                  if (isExpand) {
                    collapseExpand(shouldHandleExpandHistory);
                    return;
                  }

                  setIsExpand(true);
                  onChangeExpand?.(true);
                }}
              >
                {isExpand ? <CollapseIcon /> : <ExpandIcon />}
              </Button>
            </Tooltip>
          )}
          <Button className={'close'} onClick={onClose}>
            <CloseIcon />
          </Button>
        </Stack>
        <Box className={'modal-content visible-scroll'} ref={ref}>
          {children}
        </Box>
        {extraNode}

        {showScrollToTopButton && (
          <TopToScroll
            scrollableDivRef={contentRef}
            scrollToTopBottomOffset={scrollToTopBottomOffset}
          />
        )}
      </Box>
    );
  },
);
ModalInner.displayName = 'BoardDetailModalInner';
